import { Fragment, useEffect } from 'react';
import { RecordListPayload, RecordListType, useDataContext } from 'context/DataStoreContext';
import { useApi } from 'model/Api';
import Pager from 'component/part/Pager';

const style = {
  pager: (position: 1 | 2) =>
    position === 1
      ? { marginBottom: '1rem' }
      : { marginTop: '1rem' },
  sorter: (order?: number, defaultOrder?: number) => ({
    cursor: 'pointer',
    ['&::after']: {
      content: `"${(order ?? defaultOrder) === 1 ? '▼' : '▲'}"`,
      color: order !== undefined ? '#000000' : '#cccccc',
    },
  }),
  table: {
    borderCollapse: 'collapse' as const,
    fontSize: '1.6rem',
  },
  th: {
    padding: '1rem',
    borderBottom: 'solid 0.1rem #cccccc',
    fontSize: '1.2rem',
    fontWeight: '700',
    textAlign: 'left' as const,
    verticalAlign: 'middle' as const,
    backgroundColor: 'white',
  },
  stickyTh: {
    position: 'sticky' as const,
    left: 0,
    zIndex: 2,
  },
  td: {
    padding: '1rem',
    borderBottom: 'solid 0.1rem #cccccc',
    verticalAlign: 'middle' as const,
  },
  stickyTd: {
    position: 'sticky' as const,
    left: 0,
    zIndex: 1,
    backgroundColor: 'white',
  },
  tableRow: {
    ['&:hover td']: { backgroundColor: '#f0f0f0' },
  },
  tableRowSelected: {
    ['& td']: { backgroundColor: '#ffcccc' },
    ['&:hover td']: { backgroundColor: '#ffcccc' },
  },
};

export type ListIndex = {
  label: string | string[];
  key?: string;
  align?: 'left' | 'right' | 'center';
};

/** rows に渡す行データ。配列または { cells, selected?, onClick? } のどちらでも可 */
export type ListRow =
  | React.ReactNode[]
  | { cells: React.ReactNode[]; selected?: boolean; onClick?: () => void };

export type ListProps<T extends RecordListType> = Readonly<{
  type: T;
  indexes: ListIndex[];
  conditions?: RecordListPayload['conditions'];
  displayPager?: boolean;
  rows?: ListRow[];
  className?: string;
  /** 左端から固定表示にする列の数 */
  stickyColumns?: number;
}>;

const List = <T extends RecordListType>(props: ListProps<T>) => {
  const { state } = useDataContext();
  const api = useApi();
  const recordsState = state?.[props.type];
  const {
    page = 1,
    limit,
    perPage = 1,
    sorts = [],
  } = recordsState || {};
  const conditions = { ...recordsState?.conditions || {}, ...props.conditions || {} };
  const stickyColumns = props.stickyColumns ?? 0;

  const indexes = props.indexes.map((indexParam) => {
    const sortParam = sorts.find(({ key }) => key === indexParam.key);
    const order = sortParam?.order;
    const defaultOrder = indexParam.key === 'id' ? 1 : 0;
    const label = Array.isArray(indexParam.label)
      ? indexParam.label.map((labelText) => <span>{labelText}</span>)
      : indexParam.label;
    return { ...indexParam, label, order, defaultOrder };
  });

  const onChangePage = (page: number) => {
    api[props.type].paginate({ ...conditions, limit, sorts, page });
  };

  const onChangeSort = (sortKey: string) => {
    const sortParamIndex = sorts.findIndex(({ key }) => key === sortKey);
    if (sortParamIndex >= 0) {
      const [sortParam] = sorts.splice(sortParamIndex, 1);
      sortParam.order = 1 - sortParam.order;
      sorts.unshift(sortParam);
    } else {
      sorts.unshift({ key: sortKey, order: 0 });
    }
    api[props.type].paginate({ ...conditions, limit, sorts, page });
  };

  useEffect(() => {
    api[props.type].paginate({ ...conditions, limit, sorts, page });
  }, [props.conditions]);

  const pager = (position: 1 | 2) =>
    props.displayPager !== false ? (
      <Pager onChange={(v) => onChangePage(v)} page={page} perPage={perPage} css={style.pager(position)} />
    ) : null;

  return <Fragment>
    {pager(1)}
    <div css={{ overflowX: 'auto' }}>
      <table css={style.table} className={props.className}>
        <thead>
          <tr>
            {indexes.map((indexParam, i) => (
              <th
                key={i}
                css={[style.th, i < stickyColumns ? style.stickyTh : {}]}
              >
                <div css={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span>{indexParam.label}</span>
                  {indexParam.key && (
                    <span
                      onClick={() => onChangeSort(indexParam.key as string)}
                      css={style.sorter(indexParam.order, indexParam.defaultOrder)}
                    />
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(props.rows || []).map((row, rowIndex) => {
            const cells = Array.isArray(row) ? row : row.cells;
            const selected = Array.isArray(row) ? false : (row.selected ?? false);
            const onClick = Array.isArray(row) ? undefined : row.onClick;
            return (
              <tr
                key={rowIndex}
                onClick={onClick}
                css={[
                  style.tableRow,
                  selected ? style.tableRowSelected : {},
                  onClick ? { cursor: 'pointer' } : {},
                ]}
              >
                {cells.map((cell, cellIndex) => (
                  <td
                    key={cellIndex}
                    css={[
                      style.td,
                      cellIndex < stickyColumns ? style.stickyTd : {},
                      indexes[cellIndex]?.align ? { textAlign: indexes[cellIndex].align } : {},
                    ]}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    {pager(2)}
  </Fragment>;
};

export default List;
